'use strict';

const CartCollection = require('./collection/cart.collection').default;
const Committer = require('./utils/committer');
const builders = require('./builders/main');
const getters = require('./utils/getters');
const CATEGORIES = require('./utils/categories');
const SOURCES = require('./utils/sources');
const ACTIONS = require('./utils/actions');
const ExtraInfo = require('./collection/extra.info');

const getTimestamp = () => new Date().getTime();

const flattenArgs = (args) => Array.from(args).map((arg) => JSON.stringify(arg));

const getAction = (type, args) => ({action: type, data: flattenArgs(args), created: getTimestamp()});

const getAddedItems = ({productsToAdd = []}) => productsToAdd;

const filterByCategory = (items = [], searchedCategory) => items.filter(({category}) => category === searchedCategory);

const getProcessedProducts = ({processedProducts = []} = {}) => processedProducts;

const getData = ({data} = {}) => data;

const onChange = (subscribers = [], event) => subscribers.forEach((handler) => handler(event));

const formatId = (id) => `${id}`.trim();

class Cart {
    constructor() {
        this.cartCollection = new CartCollection();
        this.actions = [];
        this.subscribers = [];
        this.builderInstance = builders(this);
        this.gettersInstance = getters(this);
        this.loadedPromise = new Promise((resolve, reject) => {
            this.loadedResolve = resolve;
            this.loadedReject = reject;
        });
    }

    subscribe(handler = () => {}) {
        if (!this.subscribers.includes(handler)) {
            this.subscribers.push(handler);
        }
    }

    toggleProduct(data = {}, selected = false) {
        return selected
            ? this.addProduct(data)
            : this.removeProduct(data.name);
    }

    addProduct(data = {}) {
        this.actions.push(getAction(ACTIONS.ADD, arguments));
        this.cartCollection.addProduct(data);
        onChange(this.subscribers, {action: ACTIONS.ADD, data});
    }

    updateProduct(data = {}) {
        this.actions.push(getAction(ACTIONS.UPDATE, arguments));
        this.cartCollection.updateProduct(data);
        onChange(this.subscribers, {action: ACTIONS.UPDATE, data});
    }

    removeProduct(name, data = {}) {
        const product = this.cartCollection.getProduct(name) || {};
        this.actions.push(getAction(ACTIONS.REMOVE, arguments));
        this.cartCollection.removeProduct(name);
        onChange(this.subscribers, {action: ACTIONS.REMOVE, data: {...product.data, ...data}});
    }

    refresh() {
        onChange(this.subscribers, {action: ACTIONS.UPDATE, data: {}});
    }

    containsProduct(name) {
        return this.cartCollection.containsProduct(name);
    }

    hasProductBeenRemoved(name) {
        return this.cartCollection.hasProductBeenRemoved(name);
    }

    getUncommittedItems(filter = () => true) {
        return this.cartCollection.getUncommittedItems(filter);
    }

    getItems(filter = () => true) {
        return getAddedItems(this.cartCollection.getAllItems(filter));
    }

    getItemsByCategory(category, filter = () => true) {
        return filterByCategory(this.getItems(filter), category);
    }

    getActions() {
        return this.actions;
    }

    restore() {
        this.actions.push(getAction(ACTIONS.RESTORE, arguments));
        this.cartCollection.restore();
        this.loadedResolve(true);
    }

    clear() {
        this.cartCollection.clear();
    }

    commit(params) {
        this.actions.push(getAction(ACTIONS.COMMIT, arguments));

        return this.getCommitPromise(params);
    }

    commitIfNeeded(params) {
        this.actions.push(getAction(ACTIONS.COMMIT, arguments));

        return this.cartCollection.isAnythingToBeCommitted()
            ? this.getCommitPromise(params)
            : Promise.resolve({success: true});
    }

    getCommitPromise({priceBreakdownCalculationMode = 'PAYMENT_PAGE'} = {}) {
        return new Promise((resolve) => {
            Committer.commit({...this.cartCollection.commitData, priceBreakdownCalculationMode}).then((data) => {
                this.cartCollection.updateItems(getProcessedProducts(getData(data)));
                resolve(data);
            });
        });
    }

    get categories() {
        return Object.assign({}, CATEGORIES);
    }

    get sources() {
        return Object.assign({}, SOURCES);
    }

    get builders() {
        return this.builderInstance;
    }

    get take() {
        return this.gettersInstance;
    }

    setBookingId(id) {
        this.cartCollection.setBookingId(formatId(id));
    }

    updateBookingId(id) {
        this.cartCollection.updateBookingId(formatId(id));
        this.cartCollection.reprocessCommittedElements();
    }

    setBuyerInfo(info = {}) {
        this.cartCollection.setBuyerInfo(info);
    }

    get changedPolicies() {
        const allItems = this.cartCollection.getAllItems();
        const result = {
            added: {},
            removed: {},
        };
        for (let item of allItems.productsToAdd) {
            result.added[item.policy] = item;
        }
        for (let item of allItems.productsToRemove) {
            result.removed[item.policy] = item;
        }
        return result;
    }

    get whenLoaded() {
        return this.loadedPromise;
    }

    addTracking(tracking = {}) {
        ExtraInfo.addTracking(tracking);
    }

    get tracking() {
        return ExtraInfo.getTracking();
    }
}

module.exports = new Cart();
